import {useEffect, useState} from "react";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {pv} from "@pg-design/helpers-css";
import {borderRadius, p} from "@pg-design/helpers-css/";
import {BalconyTerraceIcon, ContactIcon, GardenIcon, PoiBusIcon, PropertyPlanIcon, SpecialPriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {TextareaField} from "@pg-design/inputs-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";

import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {gtmOfferOpinionsButtonClick} from "../../../tracking/google_tag_manager/gtm_event_offer_opinions";
import {usePostUserOpinionsMutation} from "../../api/offer_opinions_api";
import {hitOfferOpinionsTracking} from "../../tracking/offer_opinions_tracking";
import {OfferType} from "../../types/OfferType";
import {offerOpinionLS} from "../../utils/offer_opinion_cookies";
import {OfferDetailOpinionsFormOption} from "./OfferDetailOpinionsFormOption";

import starIcon from "./assets/star_bg.svg";

interface Props {
    onSubmit: () => void;
    offer: {
        id: number;
        type: OfferType;
    };
    property?: {
        id: number;
    };
}

export type OfferOpinionsFormFields = {
    environment_and_infrastructure: boolean;
    public_transport_access: boolean;
    investment_potential: boolean;
    architecture: boolean;
    property_layout: boolean;
    other_switch: boolean;
    other_reason: string;
};

Yup.addMethod(Yup.object, "atLeastOneOf", function (list) {
    return this.test({
        name: "atLeastOneOf",
        message: "Musisz wybrać przynajmniej jedną opcję aby wysłać formularz",
        exclusive: true,
        params: {keys: list.join(", ")},
        test: (value) => value == null || list.some((f: never) => !!value[f])
    });
});

const OfferPollFormSchema = Yup.object<OfferOpinionsFormFields>()
    .shape({
        environment_and_infrastructure: Yup.boolean(),
        public_transport_access: Yup.boolean(),
        investment_potential: Yup.boolean(),
        architecture: Yup.boolean(),
        property_layout: Yup.boolean(),
        other_switch: Yup.boolean(),
        other_reason: Yup.string().when("other_switch", {
            is: true,
            then: (schema) => schema.required("Pole wymagane").max(200, "Maksymalna długość opinii to 200 znaków"),
            otherwise: (schema) => schema.notRequired()
        })
    })
    // @ts-ignore-next-line
    .atLeastOneOf(["environment_and_infrastructure", "public_transport_access", "investment_potential", "architecture", "property_layout", "other_reason"]);

const getPlanText = (type: OfferType) => (type === OfferType.FLAT ? "Układ mieszkania" : "Plan domu");

export const OfferDetailOpinionsForm = (props: Props) => {
    const [isTextFieldVisible, setIsTextFieldVisible] = useState(false);

    const rhfForm = useFormRHF<OfferOpinionsFormFields>({
        resolver: yupResolver(OfferPollFormSchema),
        defaultValues: {},
        mode: "onChange"
    });

    const [postOfferOpinions, {isLoading}] = usePostUserOpinionsMutation();

    const onSubmit = async (formValues: OfferOpinionsFormFields) => {
        await postOfferOpinions({
            ...(props.property?.id ? {property: props.property.id} : {offer: props.offer.id}),
            ...formValues,
            other_reason: isTextFieldVisible ? formValues.other_reason : undefined
        }).finally(() => {
            const siteId = getTrackedSiteData().site_id;

            hitOfferOpinionsTracking({
                offer_id: props.property ? undefined : props.offer.id,
                property_id: props.property?.id,
                site_id: siteId,
                preferences: {
                    standard_preferences: formValues,
                    custom_preferences: formValues.other_reason
                }
            });

            offerOpinionLS.addSentOfferId(props.offer.id);
            props.onSubmit();
        });
    };

    // send form data on exit from page
    useEffect(() => {
        const handleBeforeUnload = () => {
            const formValues = rhfForm.getValues();
            if (Object.values(formValues).some((value) => value === true)) {
                onSubmit(formValues);
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isTextFieldVisible]);

    return (
        <div css={outerWrapper}>
            <FormProviderRHF {...rhfForm}>
                <form onSubmit={rhfForm.handleSubmit(onSubmit)}>
                    <div css={headingSection}>
                        <div css={iconWrapper}>
                            <Image src={starIcon} alt="Ikona Gwiazdy" width="56" height="56" />
                        </div>

                        <div css={headingsWrapper}>
                            <Text variant="headline_4" mb="1.6rem">
                                Co najbardziej podoba Ci się w tej ofercie?
                            </Text>
                            <Text variant="info_txt_1" mb="2.4rem" color={getThemeVariable("colors.gray.700")}>
                                Zaznacz te cechy, które oceniasz najwyżej. Dzięki Twojej odpowiedzi będziemy w stanie pokazać Ci lepiej dopasowane oferty.
                            </Text>
                        </div>
                    </div>

                    <div css={formWrapper}>
                        <div css={buttonsWrapper}>
                            <OfferDetailOpinionsFormOption
                                formFieldName="environment_and_infrastructure"
                                label="Otoczenie i infrastruktura"
                                icon={GardenIcon}
                            />
                            <OfferDetailOpinionsFormOption formFieldName="public_transport_access" label="Komunikacja" icon={PoiBusIcon} />
                            <OfferDetailOpinionsFormOption formFieldName="investment_potential" label="Potencjał inwestycyjny" icon={SpecialPriceIcon} />
                            <OfferDetailOpinionsFormOption formFieldName="architecture" label="Architektura budynku" icon={BalconyTerraceIcon} />
                            {props.property && (
                                <OfferDetailOpinionsFormOption formFieldName="property_layout" label={getPlanText(props.offer.type)} icon={PropertyPlanIcon} />
                            )}
                            <OfferDetailOpinionsFormOption formFieldName="other_switch" label="Inny powód" icon={ContactIcon} onClick={setIsTextFieldVisible} />
                        </div>

                        <div css={[textareaWrapper, isTextFieldVisible ? show : hide]}>
                            {isTextFieldVisible && (
                                <TextareaField
                                    {...rhfForm.register("other_reason")}
                                    error={rhfForm.formState.errors.other_reason?.message}
                                    placeholder="Jaki jest Twój powód, dla którego zdecydowałeś się na tę ofertę?"
                                    maxLength={400}
                                />
                            )}
                        </div>

                        <Button
                            variant="filled_primary"
                            onClick={() => gtmOfferOpinionsButtonClick("oceń ofertę")}
                            css={buttonStyle}
                            disabled={!rhfForm.formState.isValid}
                            isLoading={isLoading}
                        >
                            Oceń ofertę
                        </Button>
                    </div>
                </form>
            </FormProviderRHF>
        </div>
    );
};

const outerWrapper = css`
    ${borderRadius(2)}
    background-color: #fff;
    ${p(1, 2)};
    container-type: inline-size;
`;

const headingSection = css`
    display: flex;
    flex-direction: row;
    gap: calc(var(--globals-baseUnit) * 2);
`;

const iconWrapper = css`
    width: 56px;
`;

const headingsWrapper = css``;

const formWrapper = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--globals-baseUnit);
`;

const buttonsWrapper = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--globals-baseUnit);
    width: 100%;

    @container (min-width: 400px) {
        grid-template-columns: 1fr 1fr;
    }

    @container (min-width: 700px) {
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const show = css`
    transition: all 0.3s ease-out;
    max-height: 220px;
    ${pv(2)};
    opacity: 1;
`;

const hide = css`
    transition: all 0.3s ease-out;
    max-height: 0;
    ${pv(0)};
    opacity: 0;
`;

const textareaWrapper = css`
    width: 100%;
`;

const buttonStyle = css`
    width: 230px;
`;
import {useEffect, useState} from "react";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {pv} from "@pg-design/helpers-css";
import {borderRadius, p} from "@pg-design/helpers-css/";
import {BalconyTerraceIcon, ContactIcon, GardenIcon, PoiBusIcon, PropertyPlanIcon, SpecialPriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {TextareaField} from "@pg-design/inputs-module";
import {getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";

import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {gtmOfferOpinionsButtonClick} from "../../../tracking/google_tag_manager/gtm_event_offer_opinions";
import {usePostUserOpinionsMutation} from "../../api/offer_opinions_api";
import {hitOfferOpinionsTracking} from "../../tracking/offer_opinions_tracking";
import {OfferType} from "../../types/OfferType";
import {offerOpinionLS} from "../../utils/offer_opinion_cookies";
import {OfferDetailOpinionsFormOption} from "./OfferDetailOpinionsFormOption";

import starIcon from "./assets/star_bg.svg";

interface Props {
    onSubmit: () => void;
    offer: {
        id: number;
        type: OfferType;
    };
    property?: {
        id: number;
    };
}

export type OfferOpinionsFormFields = {
    environment_and_infrastructure: boolean;
    public_transport_access: boolean;
    investment_potential: boolean;
    architecture: boolean;
    property_layout: boolean;
    other_switch: boolean;
    other_reason: string;
};

Yup.addMethod(Yup.object, "atLeastOneOf", function (list) {
    return this.test({
        name: "atLeastOneOf",
        message: "Musisz wybrać przynajmniej jedną opcję aby wysłać formularz",
        exclusive: true,
        params: {keys: list.join(", ")},
        test: (value) => value == null || list.some((f: never) => !!value[f])
    });
});

const OfferPollFormSchema = Yup.object<OfferOpinionsFormFields>()
    .shape({
        environment_and_infrastructure: Yup.boolean(),
        public_transport_access: Yup.boolean(),
        investment_potential: Yup.boolean(),
        architecture: Yup.boolean(),
        property_layout: Yup.boolean(),
        other_switch: Yup.boolean(),
        other_reason: Yup.string().when("other_switch", {
            is: true,
            then: (schema) => schema.required("Pole wymagane").max(200, "Maksymalna długość opinii to 200 znaków"),
            otherwise: (schema) => schema.notRequired()
        })
    })
    // @ts-ignore-next-line
    .atLeastOneOf(["environment_and_infrastructure", "public_transport_access", "investment_potential", "architecture", "property_layout", "other_reason"]);

const getPlanText = (type: OfferType) => (type === OfferType.FLAT ? "Układ mieszkania" : "Plan domu");

export const OfferDetailOpinionsForm = (props: Props) => {
    const [isTextFieldVisible, setIsTextFieldVisible] = useState(false);

    const rhfForm = useFormRHF<OfferOpinionsFormFields>({
        resolver: yupResolver(OfferPollFormSchema),
        defaultValues: {},
        mode: "onChange"
    });

    const [postOfferOpinions, {isLoading}] = usePostUserOpinionsMutation();

    const onSubmit = async (formValues: OfferOpinionsFormFields) => {
        await postOfferOpinions({
            ...(props.property?.id ? {property: props.property.id} : {offer: props.offer.id}),
            ...formValues,
            other_reason: isTextFieldVisible ? formValues.other_reason : undefined
        }).finally(() => {
            const siteId = getTrackedSiteData().site_id;

            hitOfferOpinionsTracking({
                offer_id: props.property ? undefined : props.offer.id,
                property_id: props.property?.id,
                site_id: siteId,
                preferences: {
                    standard_preferences: formValues,
                    custom_preferences: formValues.other_reason
                }
            });

            offerOpinionLS.addSentOfferId(props.offer.id);
            props.onSubmit();
        });
    };

    // send form data on exit from page
    useEffect(() => {
        const handleBeforeUnload = () => {
            const formValues = rhfForm.getValues();
            if (Object.values(formValues).some((value) => value === true)) {
                onSubmit(formValues);
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isTextFieldVisible]);

    return (
        <div css={outerWrapper}>
            <FormProviderRHF {...rhfForm}>
                <form onSubmit={rhfForm.handleSubmit(onSubmit)}>
                    <div css={headingSection}>
                        <div css={iconWrapper}>
                            <Image src={starIcon} alt="Ikona Gwiazdy" width="56" height="56" />
                        </div>

                        <div css={headingsWrapper}>
                            <Text variant="headline_4" mb="1.6rem">
                                Co najbardziej podoba Ci się w tej ofercie?
                            </Text>
                            <Text variant="info_txt_1" mb="2.4rem" color={getThemeVariable("colors.gray.700")}>
                                Zaznacz te cechy, które oceniasz najwyżej. Dzięki Twojej odpowiedzi będziemy w stanie pokazać Ci lepiej dopasowane oferty.
                            </Text>
                        </div>
                    </div>

                    <div css={formWrapper}>
                        <div css={buttonsWrapper}>
                            <OfferDetailOpinionsFormOption
                                formFieldName="environment_and_infrastructure"
                                label="Otoczenie i infrastruktura"
                                icon={GardenIcon}
                            />
                            <OfferDetailOpinionsFormOption formFieldName="public_transport_access" label="Komunikacja" icon={PoiBusIcon} />
                            <OfferDetailOpinionsFormOption formFieldName="investment_potential" label="Potencjał inwestycyjny" icon={SpecialPriceIcon} />
                            <OfferDetailOpinionsFormOption formFieldName="architecture" label="Architektura budynku" icon={BalconyTerraceIcon} />
                            {props.property && (
                                <OfferDetailOpinionsFormOption formFieldName="property_layout" label={getPlanText(props.offer.type)} icon={PropertyPlanIcon} />
                            )}
                            <OfferDetailOpinionsFormOption formFieldName="other_switch" label="Inny powód" icon={ContactIcon} onClick={setIsTextFieldVisible} />
                        </div>

                        <div css={[textareaWrapper, isTextFieldVisible ? show : hide]}>
                            {isTextFieldVisible && (
                                <TextareaField
                                    {...rhfForm.register("other_reason")}
                                    error={rhfForm.formState.errors.other_reason?.message}
                                    placeholder="Jaki jest Twój powód, dla którego zdecydowałeś się na tę ofertę?"
                                    maxLength={400}
                                />
                            )}
                        </div>

                        <Button
                            variant="filled_primary"
                            onClick={() => gtmOfferOpinionsButtonClick("oceń ofertę")}
                            css={buttonStyle}
                            disabled={!rhfForm.formState.isValid}
                            isLoading={isLoading}
                        >
                            Oceń ofertę
                        </Button>
                    </div>
                </form>
            </FormProviderRHF>
        </div>
    );
};

const outerWrapper = css`
    ${borderRadius(2)}
    background-color: #fff;
    ${p(1, 2)};
    container-type: inline-size;
`;

const headingSection = css`
    display: flex;
    flex-direction: row;
    gap: calc(var(--globals-baseUnit) * 2);
`;

const iconWrapper = css`
    width: 56px;
`;

const headingsWrapper = css``;

const formWrapper = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--globals-baseUnit);
`;

const buttonsWrapper = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--globals-baseUnit);
    width: 100%;

    @container (min-width: 400px) {
        grid-template-columns: 1fr 1fr;
    }

    @container (min-width: 700px) {
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const show = css`
    transition: all 0.3s ease-out;
    max-height: 220px;
    ${pv(2)};
    opacity: 1;
`;

const hide = css`
    transition: all 0.3s ease-out;
    max-height: 0;
    ${pv(0)};
    opacity: 0;
`;

const textareaWrapper = css`
    width: 100%;
`;

const buttonStyle = css`
    width: 230px;
`;
